import { RefObject, useRef, useState } from 'react';

export type PhoneVerificationStep = 'phone' | 'code' | 'success';

export interface PhoneVerificationFlow {
  step: PhoneVerificationStep;
  phone: string;
  cooldownSeconds?: number;
  blockSeconds?: number;
  isLoading: boolean;
  submitButtonRef: RefObject<HTMLButtonElement | null>;
  setLoading: (isLoading: boolean) => void;
  handleCodeSent: (phone: string, cooldownSeconds?: number, blockSeconds?: number) => void;
  handleConfirmed: (isConfirmed: boolean) => void;
  goBack: () => boolean;
  submit: () => void;
  reset: () => void;
}

/**
 * Owns the phone-verification state machine: entry, then the SMS code, then success.
 */
export const usePhoneVerificationFlow = (): PhoneVerificationFlow => {
  const [step, setStep] = useState<PhoneVerificationStep>('phone');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>('');
  const [cooldownSeconds, setCooldownSeconds] = useState<number | undefined>(undefined);
  const [blockSeconds, setBlockSeconds] = useState<number | undefined>(undefined);

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const handleCodeSent = (sentPhone: string, cooldown?: number, block?: number): void => {
    setPhone(sentPhone);
    setCooldownSeconds(cooldown);
    setBlockSeconds(block);
    setStep('code');
  };

  const handleConfirmed = (isConfirmed: boolean): void => {
    if (!isConfirmed) {
      return;
    }
    setStep('success');
  };

  const goBack = (): boolean => {
    if (step === 'code') {
      setStep('phone');
      return true;
    }
    return false;
  };

  const submit = (): void => {
    submitButtonRef.current?.click();
  };

  const reset = (): void => {
    setStep('phone');
    setLoading(false);
    setPhone('');
    setCooldownSeconds(undefined);
    setBlockSeconds(undefined);
  };

  return {
    step: step,
    phone: phone,
    cooldownSeconds: cooldownSeconds,
    blockSeconds: blockSeconds,
    isLoading: isLoading,
    submitButtonRef: submitButtonRef,
    setLoading: setLoading,
    handleCodeSent: handleCodeSent,
    handleConfirmed: handleConfirmed,
    goBack: goBack,
    submit: submit,
    reset: reset,
  };
};
